"use client";

import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Box, Stack, Typography } from "@mui/material";
import { Trans } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useWantToBuyStore } from "@/contexts/useWantToBuy";
import { useTranslation } from "@/i18n/client";
import color from "@/styles/color";
import { thousandComma } from "@/libs/common/string";
import { useReadScreen } from "@/hooks/useReadScreen.hook";
import { usePageLoadingStore } from "@/contexts/usePageLoading";
import { VehicleSortEnum } from "@/libs/enum/wtb-query-key.enum";
import type { WTBViewPointType } from "@/hooks/want-to-buy/useCarFilter.hook";
import type { VehicleSearchParams } from "@/libs/types/vehicle.type";
import { OrderByItems } from "@/libs/constant/want-to-buy.constant";
import { getCurrentLocation } from "@/libs/common/location";
import { DealerKeys } from "@/libs/enum/dealer-query.enum";
import { DealerServices } from "@/libs/services/dealer";
import { MapProvider } from "@/components/providers/MapProvider";
import { UsedCarFilter, UsedCarFilterNearby } from "@/libs/constant/key-localstorage.constant";
import { setCookieFromServer, deleteCookieFromServer } from "@/app/api/cookies/cookie";

import NextDynamic from "next/dynamic";

const CheckboxWTB = NextDynamic(() => import("@/components/Checkbox/CheckboxWTB"), {
  ssr: false,
});

const MenuSelectWTB = NextDynamic(() => import("@/components/MenuSelect/MenuSelectWTB"), {
  ssr: false,
});

const SwitchWTB = NextDynamic(() => import("@/components/Switch/SwitchWTB"), {
  ssr: false,
});

const DrawerFilter = NextDynamic(() => import("./components/DrawerFilter"), {
  ssr: false,
});

const IconLoader = NextDynamic(() => import("@/components/IconLoader"), {
  ssr: false,
});

const WantToBuySearchInput = NextDynamic(() => import("./components/WantToBuySearchInput"), {
  ssr: false,
});

const MenuRadioItem = NextDynamic(() => import("@/components/MenuSelect/MenuRadioItem"), {
  ssr: false,
});


type WantToBuyViewAndFilterProps = {
  count: number;
};

type SortParams = Pick<VehicleSearchParams, "SortBy" | "SortDirection">;

export const ViewpointItems: { value: WTBViewPointType; icon: JSX.Element }[] = [
  {
    value: "vertical",
    icon: <IconLoader iconName="ViewPointVerticalIcon" />,
  },
  {
    value: "horizontal",
    icon: <IconLoader iconName="ViewPointHorizontalIcon" />,
  },
];

const WantToBuyViewAndFilter: FC<WantToBuyViewAndFilterProps> = ({ count }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isLessThanLg } = useReadScreen();

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState<boolean>(false);

  const { setLoading } = usePageLoadingStore();
  const { viewFilter, setViewFilter } = useWantToBuyStore();

  const currentLocation = useMemo(async () => {
    const result = await getCurrentLocation()
    return result
  }, []);

  const { data: responseDealerLocation } = useQuery({
    queryKey: [DealerKeys.DEALER_LOCALTION_LIST],
    queryFn: () =>
      DealerServices.getDealerList({
        SearchText: "DealerMap",
      }).then((res) => {
        return res;
      }),
  });

  const updateQueryParams = useCallback(
    (
      params: Record<string, string | boolean | number | undefined>,
      searchParams: URLSearchParams,
      router: ReturnType<typeof useRouter>,
    ) => {
      const currentParams = new URLSearchParams(searchParams.toString()); // Clone current query string
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          currentParams.set(key, value as string); // Update or add a new param
        } else {
          currentParams.delete(key); // Delete a param if needed
        }
        setCookieFromServer(UsedCarFilter, JSON.stringify({ ...viewFilter, [key]: value }), { httpOnly: true, secure: true });
      });
      currentParams.delete("page");
      setLoading();
      const newUrl = `${pathname}?${currentParams.toString()}`;
      router.replace(newUrl);
      router.refresh();
    },
    [pathname, setLoading, viewFilter],
  );

  const calculateDealerDistance = useCallback(
    ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      const setDealerNearList: number[] = [];
      responseDealerLocation?.DealerInfo?.forEach((item) => {
        if (!+item.Latitude || !+item.Longtitude) return;
        const { Latitude, Longtitude } = item;
        const lat = +Latitude;
        const lng = +Longtitude;

        const distanceInMeters = google.maps.geometry?.spherical?.computeDistanceBetween(
          { lat: latitude, lng: longitude },
          { lat, lng },
        );

        const distance = Math.round(distanceInMeters / 1000);
        if (distance < 200) {
          setDealerNearList.push(item.DealerId);
        }
      });
      const dealerNearList = Array.from(new Set(setDealerNearList));
      setCookieFromServer(UsedCarFilterNearby, JSON.stringify(dealerNearList), { httpOnly: true, secure: true });
      return;
    },
    [responseDealerLocation],
  );

  const handleFindNearby = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        currentLocation.then(({ latitude, longitude }) => {
          calculateDealerDistance({ latitude, longitude });
        });
      } else {
        deleteCookieFromServer(UsedCarFilterNearby);
      }
      updateQueryParams({ isNearMe: isChecked }, searchParams, router);
    },
    [calculateDealerDistance, currentLocation, router, searchParams, updateQueryParams],
  );

  const onChangeViewFilter = useCallback(
    async (value: string | boolean, key: keyof typeof viewFilter) => {
      setViewFilter({ ...viewFilter, [key]: value });
      if (["isVideo"].includes(key)) {
        updateQueryParams({ [key]: value }, searchParams, router);
      }
      if (key === "isNearMe") {
        handleFindNearby(value as boolean);
      }
    },
    [updateQueryParams, viewFilter, router, searchParams, setViewFilter, handleFindNearby],
  );

  const onChangeSort = useCallback(
    (sort: VehicleSortEnum, params: SortParams) => {
      setViewFilter({
        ...viewFilter,
        sort,
      });
      updateQueryParams(
        { SortBy: params.SortBy, SortDirection: params.SortDirection },
        searchParams,
        router,
      );
    },
    [updateQueryParams, viewFilter, router, searchParams, setViewFilter],
  );

  const onClickOpenMobileFilter = useCallback(() => {
    setIsOpenDrawerFilter(true);
  }, []);

  return (
    <>
      <Stack gap={{ xs: 2 }}>
        {isLessThanLg && <WantToBuySearchInput />}
        <Stack gap={{ xs: 1 }}>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              order: { xs: 1, xl: 2 },
              justifyContent: "space-between",
            }}
          >
            <MapProvider noWait>
              <Box
                sx={{
                  height: "40px",
                  width: { xs: "unset", sm: "100%" },
                  display: "flex",
                  alignItems: "center",
                  bgcolor: { sm: color.white },
                  border: {
                    xs: "unset",
                    sm: "1px solid #E1E1E1",
                  },
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                <SwitchWTB
                  defaultChecked={viewFilter.isNearMe}
                  name="near-me"
                  onChange={(e) => {
                    onChangeViewFilter(e.target.checked, "isNearMe");
                  }}
                  checked={viewFilter?.isNearMe ?? false}
                />
                <Typography
                  sx={{
                    fontWeight: 400,
                    color: color.black,
                    fontSize: { xs: "12px", sm: "14px", xl: "16px" },
                    lineHeight: {
                      xs: "16px",
                      sm: "21px",
                      xl: "24px",
                    },
                  }}
                >
                  {t("W2B.filter.near-me")}
                </Typography>
              </Box>
            </MapProvider>
            <Box
              sx={{
                height: "40px",
                width: { xs: "unset", sm: "100%" },
                display: "flex",
                gap: "8px",
                alignItems: "center",
                bgcolor: {
                  xs: "unset",
                  sm: color.white,
                },
                border: {
                  xs: "unset",
                  sm: "1px solid #E1E1E1",
                },
                borderRadius: "8px",
                padding: "8px",
              }}
            >
              <CheckboxWTB
                name="have-video"
                defaultChecked={viewFilter.isVideo}
                onChange={(e) => {
                  onChangeViewFilter(e.target.checked, "isVideo");
                }}
                checked={viewFilter.isVideo ?? false}
              />
              <Typography
                sx={{
                  fontWeight: 400,
                  color: color.black,
                  fontSize: { xs: "12px", sm: "14px", xl: "16px" },
                  lineHeight: {
                    xs: "21px",
                    xl: "24px",
                  },
                }}
              >
                {t("W2B.filter.has-video")}
              </Typography>
            </Box>
            <Box
              sx={{
                height: "40px",
                width: { xs: "unset", sm: "100%" },
                display: "flex",
                gap: "8px",
                alignItems: "center",
                bgcolor: {
                  xs: color.white,
                },
                border: "1px solid #E1E1E1",
                borderRadius: "8px",
                padding: "8px",
              }}
            >
              <MenuSelectWTB
                name="sort-by-desk"
                title={
                  <Typography
                    sx={{
                      fontWeight: 400,
                      color: color.black,
                      fontSize: {
                        xs: "12px",
                        sm: "14px",
                        xl: "16px",
                      },
                      lineHeight: {
                        xs: "16px",
                        sm: "21px",
                        xl: "24px",
                      },
                    }}
                  >
                    {t(`W2B.sort.${viewFilter.sort}`)}
                  </Typography>
                }
                defaultValue={viewFilter.sort}
              >
                {OrderByItems.map((item, index) => (
                  <MenuRadioItem
                    key={`order-by-item-${index + 1}`}
                    iconActionName={`sort-by-action-${index}`}
                    label={t(`W2B.sort.${item.title}`)}
                    value={item.title}
                    onClick={() => onChangeSort(item.title, item.params)}
                  />
                ))}
              </MenuSelectWTB>
            </Box>
            <Box
              sx={{
                height: "40px",
                width: { xs: "unset", sm: "100%" },
                display: { xs: "none", sm: "flex" },
                gap: "8px",
                alignItems: "center",
                bgcolor: { xs: "unset", sm: color.white, lg: color.white, xl: color.white },
                border: {
                  xs: "unset",
                  sm: "1px solid #E1E1E1",
                },
                borderRadius: "8px",
                padding: "8px",
              }}
            >
              <MenuSelectWTB
                name="view-point-desk"
                title={
                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    {ViewpointItems.find((item) => item.value === viewFilter?.viewMode)?.icon}
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: color.black,
                        fontSize: { xs: "14px", md: "14px", xl: "16px" },
                        lineHeight: { xs: "21px", xl: "24px" },
                      }}
                    >
                      {t(`W2B.filter.view-${viewFilter.viewMode}`)}
                    </Typography>
                  </Box>
                }
                defaultValue={viewFilter?.viewMode}
              >
                {ViewpointItems.map((item, index) => (
                  <MenuRadioItem
                    key={`order-by-item-${index + 1}`}
                    iconActionName={`view-point-action-${index}`}
                    label={t(`W2B.filter.view-${item.value}`)}
                    value={item.value}
                    onClick={() => onChangeViewFilter(item.value, "viewMode")}
                  />
                ))}
              </MenuSelectWTB>
            </Box>
          </Box>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              order: { xs: 2, lg: 1 },
            }}
          >
            <Typography
              sx={{
                color: { xs: color.gray100, lg: color.black },
                fontWeight: { xs: 400, lg: 600 },
                fontSize: { xs: "0.75rem", lg: "1rem" },
                lineHeight: { xs: "20px", lg: "24px" },
              }}
            >
              <Trans i18nKey="W2B.search-result" values={{ total: thousandComma(count) }} />
            </Typography>
            <Box
              sx={{
                display: { xs: "flex", lg: "none" },
                flexDirection: "row",
                alignItems: "center",
                gap: 0.5,
              }}
              onClick={onClickOpenMobileFilter}
            >
              <IconLoader iconName="FilterControlGradientIcon" />
              <Typography
                sx={{
                  background: color.redGradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("W2B.filter.title-mobile")}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Stack>
      {isLessThanLg && isOpenDrawerFilter && (
        <DrawerFilter isOpen={true} onClose={() => setIsOpenDrawerFilter(false)} />
      )}
    </>
  );
};

export default WantToBuyViewAndFilter;
